/****************
 * Scrolling, for a page where the window is not the thing that scrolls.
 *
 * An ancestor of the app carries `overflow-hidden`, so two obvious approaches
 * both silently do nothing:
 *
 *   - `scrollIntoView` walks up to the nearest scroll container and adjusts
 *     THAT one — which cannot scroll — instead of the page.
 *   - `window.scrollTo` moves the window, which is not what is scrolling.
 *
 * Both were shipped and both failed in exactly that way (2026-09-02/03). So
 * rather than assume, ask the DOM which element is actually scrollable and
 * move that one.
 ****************/

/** The nearest ancestor that genuinely scrolls, or null if the document does. */
export function scrollerFor(element: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = element.parentElement;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (
      /(auto|scroll|overlay)/.test(style.overflowY) &&
      node.scrollHeight > node.clientHeight + 4
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Put `element` in the middle of whatever is scrolling it.
 * Returns the element's distance from the top of the viewport, so a caller can
 * tell whether the page has stopped moving under it.
 */
export function centreInView(element: HTMLElement, behavior: ScrollBehavior = 'smooth'): number {
  const rect = element.getBoundingClientRect();
  const scroller = scrollerFor(element);

  if (scroller) {
    const box = scroller.getBoundingClientRect();
    scroller.scrollTo({
      top: Math.max(
        0,
        scroller.scrollTop + rect.top - box.top - scroller.clientHeight / 2 + rect.height / 2,
      ),
      behavior,
    });
  } else {
    const doc = document.scrollingElement || document.documentElement;
    doc.scrollTo({
      top: Math.max(0, doc.scrollTop + rect.top - window.innerHeight / 2 + rect.height / 2),
      behavior,
    });
  }

  return rect.top;
}

/**
 * Back to the top, on every plausible scroller.
 *
 * Deliberately not clever: it moves the document AND any element that is
 * currently scrolled, because which one the layout uses has changed before and
 * will change again. Cheap, and it cannot half-work.
 *
 * INCLUDING overflow-hidden ones (11-02, Guillaume: "most of the time it does,
 * sometime it does not"). The page column and the app root are overflow-hidden;
 * a user cannot scroll them, but scrollIntoView can and does (the chat dock on
 * each new message, the guides, the Copy Trade trader list). Once moved, every
 * page after that opened shifted, and this skipped them because it only reset
 * auto/scroll containers. The side menu keeps its own scroll.
 */
export function scrollPageToTop() {
  const doc = document.scrollingElement || document.documentElement;
  doc.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  for (const node of Array.from(document.querySelectorAll<HTMLElement>('*'))) {
    if (node.scrollTop <= 0 && node.scrollLeft <= 0) continue;
    if (node.closest('[data-sidebar="sidebar"], [role="dialog"], [data-keep-scroll]')) continue;
    const style = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY)) node.scrollTop = 0;
    // A container nobody can scroll by hand was moved by script: put it back whole.
    else if (/(hidden|clip)/.test(style.overflowY) || /(hidden|clip)/.test(style.overflowX)) {
      node.scrollTop = 0;
      node.scrollLeft = 0;
    }
  }
}

/**
 * Bring `element` into view inside its OWN list only (the nearest scroller),
 * never moving the page around it. scrollIntoView moves every ancestor,
 * overflow-hidden ones included, which is what shifted the whole layout.
 */
export function revealInList(element: HTMLElement): void {
  const scroller = scrollerFor(element);
  if (!scroller) return;
  const box = scroller.getBoundingClientRect();
  const rect = element.getBoundingClientRect();
  if (rect.top < box.top) scroller.scrollTop += rect.top - box.top;
  else if (rect.bottom > box.bottom) scroller.scrollTop += rect.bottom - box.bottom;
}

/**
 * LAND ON ONE ROW AND STAY ON IT (Guillaume 11-03: "it bring me to the right page
 * but does not automatically scroll down to the proper place").
 *
 * The old landing scrolled smoothly, re-started that smooth scroll every 250 ms
 * while the page grew, and stopped for good once two reads matched -- often in
 * the middle of a smooth scroll, or before the charts above the table had
 * loaded and pushed the row down a screen. On a real session (charts, calendar,
 * equity curve all resolving on their own queries) it ended off target.
 *
 * Now: an instant jump (no animation to interrupt), and for `ms` after the row
 * first appears it is put back in the middle whenever the page moves it more
 * than a few pixels. The person's own wheel, touch or key ends it at once, so it
 * never fights them. `onFound` fires once, when the row first exists (the glow
 * starts then, not before the row is on screen).
 */
export function landOnRow(getRow: () => HTMLElement | null, onFound?: () => void, ms = 6000): () => void {
  let found = false;
  let done = false;
  let until = 0;
  const finish = () => { done = true; };
  const events: (keyof WindowEventMap)[] = ['wheel', 'touchstart', 'keydown', 'mousedown'];
  events.forEach((e) => window.addEventListener(e, finish, { passive: true, capture: true }));
  const tick = () => {
    if (done) return;
    const row = getRow();
    if (!row) return;
    if (!found) {
      found = true;
      until = Date.now() + ms;
      onFound?.();
    }
    if (Date.now() > until) { finish(); return; }
    const rect = row.getBoundingClientRect();
    const scroller = scrollerFor(row);
    const box = scroller ? scroller.getBoundingClientRect() : { top: 0, height: window.innerHeight };
    const off = rect.top + rect.height / 2 - (box.top + box.height / 2);
    if (Math.abs(off) > 12) centreInView(row, 'auto');
  };
  tick();
  const iv = setInterval(tick, 150);
  const giveUp = setTimeout(finish, ms + 8000); // the row never came: stop looking
  return () => {
    done = true;
    clearInterval(iv);
    clearTimeout(giveUp);
    events.forEach((e) => window.removeEventListener(e, finish, { capture: true }));
  };
}
